// apps/office/src/pages/office/InteriorQuotationForm.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Package, Trash2, Save, Loader2, Home, Building2,
  User, Phone, MapPin, FileText, Copy, Clock, Image as ImageIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const rs = (v) => {
  const n = Number(v) || 0;
  return `₹ ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
};

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createEmptyItem = () => ({
  id: uid(),
  productId: '',
  length: '',
  width: '',
  customRate: '',
  notes: '',
});

const createEmptyBlock = () => ({
  id: uid(),
  groupId: '',
  groupName: '',
  roomLength: '',
  roomWidth: '',
  subcategoryId: '',
  notes: '',
  items: [createEmptyItem()],
});

/* Convert saved data back to form state */
const deserializeBlocks = (savedItems) => {
  if (!Array.isArray(savedItems) || savedItems.length === 0) {
    return [createEmptyBlock()];
  }
  return savedItems.map((block) => ({
    id: block.id || uid(),
    groupId: block.groupId || '',
    groupName: block.groupName || '',
    roomLength: block.roomLength ?? '',
    roomWidth: block.roomWidth ?? '',
    subcategoryId: block.subcategoryId || '',
    notes: block.notes || '',
    items: (block.products || block.items || []).length > 0
      ? (block.products || block.items).map((p) => ({
          id: p.id || uid(),
          productId: p.productId || '',
          length: p.length ?? '',
          width: p.width ?? '',
          customRate: '',
          notes: p.notes || '',
        }))
      : [createEmptyItem()],
  }));
};

const InteriorQuotationForm = () => {
  /* ═══════════════════════════════════════════════════════════
     URL PARAMS
     - new mode:     /office/interior-quotations/new/:projectId
     - edit mode:    /office/interior-quotations/edit/:quoteId
     ═══════════════════════════════════════════════════════════ */
  const { projectId: urlProjectId, quoteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!quoteId;

  /* ═══════════════════════════════════════════════════════════
     STATE
     ═══════════════════════════════════════════════════════════ */
  const [categoryType, setCategoryType] = useState('residential');
  const [mainCategory, setMainCategory] = useState('Standard');
  const [blocks, setBlocks] = useState([createEmptyBlock()]);
  const [notes, setNotes] = useState('');
  const [quoteNumber, setQuoteNumber] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [savedQuoteId, setSavedQuoteId] = useState(quoteId || null);
  const [autoSaveCount, setAutoSaveCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  /* Refs for auto-save closure */
  const autoSaveRef = useRef(null);
  const blocksRef = useRef(blocks);
  const stateRef = useRef({});

  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  useEffect(() => {
    stateRef.current = { categoryType, mainCategory, notes, quoteNumber };
  }, [categoryType, mainCategory, notes, quoteNumber]);

  /* ═══════════════════════════════════════════════════════════
     QUERIES
     ═══════════════════════════════════════════════════════════ */

  /* Existing quotation (edit mode only) — MUST be first */
  const { data: existingQuote, isLoading: quoteLoading } = useQuery({
    queryKey: ['interior-quotation-edit', quoteId],
    queryFn: () => pb.collection('interior_quotations').getOne(quoteId),
    enabled: isEditing,
    retry: 1,
  });

  /* Effective project ID — from URL (new) or from existing quote (edit) */
  const effectiveProjectId = isEditing
    ? existingQuote?.projectId
    : urlProjectId;

  /* Project */
  const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['interior-project', effectiveProjectId],
    queryFn: () => pb.collection('interior_projects').getOne(effectiveProjectId),
    enabled: !!effectiveProjectId,
    retry: 1,
  });

  /* Client */
  const { data: client } = useQuery({
    queryKey: ['interior-quotation-client', project?.clientId],
    queryFn: () => pb.collection('leads').getOne(project.clientId),
    enabled: !!project?.clientId,
    retry: 1,
  });

  /* Groups */
  const { data: groups = [] } = useQuery({
    queryKey: ['interior-groups'],
    queryFn: () =>
      pb.collection('interior_product_groups').getFullList({
        sort: 'sortOrder,name',
        filter: 'isActive = true',
      }),
    staleTime: 60000,
  });

  /* Subcategories */
  const { data: subcategories = [] } = useQuery({
    queryKey: ['interior-subcategories'],
    queryFn: () =>
      pb.collection('interior_subcategories').getFullList({
        sort: 'sortOrder,name',
        filter: 'isActive = true',
      }),
    staleTime: 60000,
  });

  /* Products (filtered by categoryType) */
  const { data: allProducts = [] } = useQuery({
    queryKey: ['interior-products-for-quote', categoryType],
    queryFn: () =>
      pb.collection('interior_products').getFullList({
        filter: `(categoryType = "${categoryType}" || categoryType = "both") && isActive = true`,
        sort: 'itemId',
      }),
    staleTime: 60000,
  });

  const productsById = useMemo(() => {
    const m = {};
    allProducts.forEach((p) => { m[p.id] = p; });
    return m;
  }, [allProducts]);

  const subcatsById = useMemo(() => {
    const m = {};
    subcategories.forEach((s) => { m[s.id] = s; });
    return m;
  }, [subcategories]);

  /* ═══════════════════════════════════════════════════════════
     POPULATE FORM (edit mode) — runs ONCE per quoteId
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!isEditing) {
      setLoaded(true);
      return;
    }
    if (!existingQuote) return;
    if (loaded) return;

    setCategoryType(existingQuote.categoryType || 'residential');
    setMainCategory(existingQuote.mainCategory || 'Standard');
    setNotes(existingQuote.notes || '');
    setQuoteNumber(existingQuote.quoteNumber || '');
    setSavedQuoteId(existingQuote.id);
    setBlocks(deserializeBlocks(existingQuote.items));
    setLoaded(true);

    console.log('[EditForm] Loaded quotation:', {
      quoteNumber: existingQuote.quoteNumber,
      blocks: existingQuote.items?.length,
      projectId: existingQuote.projectId,
    });
  }, [existingQuote, isEditing, loaded]);

  /* ═══════════════════════════════════════════════════════════
     AUTO-GENERATE QUOTE NUMBER (new mode)
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (isEditing || quoteNumber) return;
    (async () => {
      try {
        const list = await pb.collection('interior_quotations').getFullList({
          sort: '-created', fields: 'quoteNumber',
        });
        let max = 99100;
        list.forEach((q) => {
          const m = (q.quoteNumber || '').match(/IQ(\d+)/);
          if (m) max = Math.max(max, parseInt(m[1], 10));
        });
        setQuoteNumber(`IQ${max + 1}`);
      } catch {
        setQuoteNumber('IQ99101');
      }
    })();
  }, [isEditing, quoteNumber]);

  /* ═══════════════════════════════════════════════════════════
     INHERIT DESIGN STYLE FROM PROJECT (new mode)
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (isEditing || !project) return;
    if (project.designStyle) {
      const s = project.designStyle.toLowerCase().includes('luxury') ? 'Luxury' : 'Standard';
      setMainCategory(s);
    }
  }, [project, isEditing]);

  /* ═══════════════════════════════════════════════════════════
     BLOCK / ITEM HELPERS
     ═══════════════════════════════════════════════════════════ */
  const addBlock = () => setBlocks((prev) => [...prev, createEmptyBlock()]);

  const removeBlock = (id) => {
    if (blocks.length <= 1) return toast.error('At least one block required');
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const duplicateBlock = (id) => {
    const orig = blocks.find((b) => b.id === id);
    if (!orig) return;
    setBlocks((prev) => [
      ...prev,
      { ...orig, id: uid(), items: orig.items.map((i) => ({ ...i, id: uid() })) },
    ]);
  };

  const updateBlock = (id, patch) =>
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const addItem = (blockId) =>
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === blockId ? { ...b, items: [...b.items, createEmptyItem()] } : b
      )
    );

  const updateItem = (blockId, itemId, patch) =>
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === blockId
          ? { ...b, items: b.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)) }
          : b
      )
    );

  const removeItem = (blockId, itemId) =>
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === blockId
          ? { ...b, items: b.items.filter((i) => i.id !== itemId) }
          : b
      )
    );

  /* ═══════════════════════════════════════════════════════════
     CALCULATIONS
     ═══════════════════════════════════════════════════════════ */
  const getRoomArea = (block) => {
    const l = parseFloat(block.roomLength) || 0;
    const w = parseFloat(block.roomWidth) || 0;
    return Number((l * w).toFixed(2));
  };

  const getProductArea = (item) => {
    const l = parseFloat(item.length) || 0;
    const w = parseFloat(item.width) || 0;
    return Number((l * w).toFixed(2));
  };

  const getItemRate = (block, item) => {
    if (item.customRate !== '' && item.customRate !== null) {
      return Number(item.customRate) || 0;
    }
    const product = productsById[item.productId];
    if (!product || !block.subcategoryId) return 0;
    const style = mainCategory.toLowerCase();
    return Number(product.prices?.[style]?.[block.subcategoryId]) || 0;
  };

  const getItemBaseQty = (item) => {
    const product = productsById[item.productId];
    if (!product) return 0;
    if (product.unit === 'sq.ft') return getProductArea(item);
    if (product.unit === 'running ft') return parseFloat(item.length) || 0;
    return 1;
  };

  const getItemAmount = (block, item) => {
    const rate = getItemRate(block, item);
    const qty = getItemBaseQty(item);
    return Number((rate * qty).toFixed(2));
  };

  const getBlockSubtotal = (block) =>
    block.items.reduce((s, item) => s + getItemAmount(block, item), 0);

  const grandTotal = useMemo(
    () => blocks.reduce((s, b) => s + getBlockSubtotal(b), 0),
    [blocks, productsById, mainCategory]
  );

  /* ═══════════════════════════════════════════════════════════
     SAVE
     ═══════════════════════════════════════════════════════════ */
  const performSave = useCallback(
    async (status = 'Draft', { silent = false } = {}) => {
      const currentBlocks = blocksRef.current;
      const state = stateRef.current;

      const validBlocks = currentBlocks.filter(
        (b) => b.groupId && b.subcategoryId && b.items.some((i) => i.productId)
      );

      if (validBlocks.length === 0) {
        if (!silent) toast.error('Add at least one valid block with product');
        return null;
      }

      if (!silent) setIsSaving(true);

      try {
        const serialized = validBlocks.map((block) => ({
          id: block.id,
          groupId: block.groupId,
          groupName: block.groupName || '',
          roomLength: parseFloat(block.roomLength) || 0,
          roomWidth: parseFloat(block.roomWidth) || 0,
          roomArea: getRoomArea(block),
          subcategoryId: block.subcategoryId,
          subcategoryName: subcatsById[block.subcategoryId]?.name || '',
          notes: block.notes || '',
          products: block.items
            .filter((i) => i.productId)
            .map((i) => {
              const product = productsById[i.productId];
              return {
                id: i.id,
                productId: i.productId,
                itemId: product?.itemId || '',
                name: product?.name || '',
                unit: product?.unit || '',
                image: product?.image || '',
                length: parseFloat(i.length) || 0,
                width: parseFloat(i.width) || 0,
                area: getProductArea(i),
                rate: getItemRate(block, i),
                amount: getItemAmount(block, i),
                notes: i.notes || '',
              };
            }),
        }));

        const payload = {
          quoteNumber: state.quoteNumber,
          projectId: effectiveProjectId,
          clientId: project?.clientId || '',
          categoryType: state.categoryType,
          mainCategory: state.mainCategory,
          items: serialized,
          profitMargin: existingQuote?.profitMargin || 20,
          subtotal: grandTotal,
          cgstEnabled: existingQuote?.cgstEnabled || false,
          cgstRate: existingQuote?.cgstRate || 9,
          cgstAmount: existingQuote?.cgstAmount || 0,
          sgstEnabled: existingQuote?.sgstEnabled || false,
          sgstRate: existingQuote?.sgstRate || 9,
          sgstAmount: existingQuote?.sgstAmount || 0,
          transportation: existingQuote?.transportation || 0,
          deepCleaning: existingQuote?.deepCleaning || 0,
          discountType: existingQuote?.discountType || 'percentage',
          discountValue: existingQuote?.discountValue || 0,
          discountAmount: existingQuote?.discountAmount || 0,
          total: grandTotal,  /* ← FIXED: always recalculated */
          status,
          notes: state.notes,
          isLatest: true,
        };

        console.log('[EditForm] SAVE payload summary:', {
          quoteNumber: payload.quoteNumber,
          mainCategory: payload.mainCategory,
          categoryType: payload.categoryType,
          subtotal: payload.subtotal,
          total: payload.total,
          blockCount: payload.items.length,
          firstBlockProducts: payload.items[0]?.products?.length || 0,
          firstProductRate: payload.items[0]?.products?.[0]?.rate,
          firstProductAmount: payload.items[0]?.products?.[0]?.amount,
        });

        let record;
        if (isEditing) {
          /* Update existing */
          record = await pb.collection('interior_quotations').update(quoteId, payload);
          console.log('[EditForm] Updated:', record.quoteNumber, 'total:', record.total, 'mainCat:', record.mainCategory);
        } else {
          /* Create first time */
          if (user?.id) payload.createdBy = user.id;
          record = await pb.collection('interior_quotations').create(payload);
          setSavedQuoteId(record.id);
          console.log('[EditForm] Created:', record.quoteNumber);

          /* Replace URL to edit route (so refresh keeps state) */
          try {
            window.history.replaceState({}, '', `/office/interior-quotations/edit/${record.id}`);
          } catch {}
        }

        setLastSavedAt(new Date());
        queryClient.invalidateQueries(['interior-quotations', effectiveProjectId]);
        queryClient.setQueryData(['interior-quotation-edit', record.id], record);

        if (!silent) {
          toast.success(isEditing ? '✅ Quotation updated' : '✅ Quotation saved');
        } else {
          setAutoSaveCount((c) => c + 1);
        }
        return record;
      } catch (err) {
        let msg = err.message || 'Save failed';
        if (err.originalError?.data?.message) msg = err.originalError.data.message;
        if (!silent) toast.error('❌ ' + msg);
        return null;
      } finally {
        if (!silent) setIsSaving(false);
      }
    },
    [
      effectiveProjectId, project, existingQuote, grandTotal,
      subcatsById, productsById, isEditing, quoteId, user,
    ]
  );

  const handleSave = (status = 'Draft') => performSave(status, { silent: false });

  /* ═══════════════════════════════════════════════════════════
     AUTO-SAVE every 600s
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);

    autoSaveRef.current = setInterval(() => {
      const hasContent = blocksRef.current.some(
        (b) => b.groupId && b.items.some((i) => i.productId)
      );
      if (!hasContent) return;
      performSave('Draft', { silent: true });
    }, 600000); /* 600 seconds */

    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [performSave]);

  /* ═══════════════════════════════════════════════════════════
     BACK — silent save + navigate to list
     ═══════════════════════════════════════════════════════════ */
  const handleBack = async () => {
    const hasContent = blocksRef.current.some(
      (b) => b.groupId && b.items.some((i) => i.productId)
    );
    if (hasContent) {
      await performSave('Draft', { silent: true });
    }
    if (effectiveProjectId) {
      navigate(`/office/interior-quotations/project/${effectiveProjectId}`);
    } else {
      navigate('/office/interior-projects');
    }
  };

  /* ═══════════════════════════════════════════════════════════
     GUARDS
     ═══════════════════════════════════════════════════════════ */
  if (!isEditing && !urlProjectId) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-lg mx-auto bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
          <p className="text-amber-700 font-bold text-lg mb-2">Invalid URL</p>
          <p className="text-sm text-amber-600 mb-4">No project ID in URL</p>
          <Button onClick={() => navigate('/office/interior-projects')} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        </div>
      </div>
    );
  }

  /* Loading: quote (edit) or project (new) */
  const reallyLoading =
    (isEditing && !existingQuote && quoteLoading) ||
    (!isEditing && !!urlProjectId && !project && !projectError && projectLoading) ||
    (isEditing && !!existingQuote && !loaded);

  if (reallyLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-96 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        <p className="text-sm text-slate-500">
          {isEditing ? 'Loading quotation…' : 'Loading project…'}
        </p>
      </div>
    );
  }

  /* Error: quote not found */
  if (isEditing && !existingQuote && !quoteLoading) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-lg mx-auto bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <p className="text-red-700 font-bold text-lg mb-2">Quotation not found</p>
          <p className="text-sm text-red-600 mb-4">
            The quotation you're trying to edit doesn't exist or was deleted.
          </p>
          <Button onClick={() => navigate('/office/interior-projects')} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  /* Error: project failed */
  if (projectError || (!isEditing && !project)) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-lg mx-auto bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <p className="text-red-700 font-bold text-lg mb-2">Cannot open form</p>
          <p className="text-sm text-red-600 mb-4">
            {projectError?.message || 'Project not found'}
          </p>
          <Button onClick={() => navigate('/office/interior-projects')} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6 p-4 md:p-6 pb-32">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 sm:p-8 shadow-xl">
        <div className="relative flex items-start gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={handleBack}
            className="text-white/80 hover:text-white hover:bg-white/20 -ml-2 mt-1 shrink-0"
            title="Back to quotation list"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <FileText className="w-7 h-7" /> {isEditing ? 'Edit Quotation' : 'New Quotation'}
            </h1>
            <p className="text-white/80 text-sm mt-1">
              <span className="font-mono font-bold">{quoteNumber || 'Generating…'}</span>
              {project?.propertyName && ` · ${project.propertyName}`}
            </p>
          </div>
          {lastSavedAt && (
            <div className="hidden sm:flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-lg px-3 py-1.5 text-white/90 text-xs">
              <Clock className="w-3.5 h-3.5" />
              <span>
                Saved {lastSavedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </span>
              {autoSaveCount > 0 && (
                <span className="ml-1 text-[10px] opacity-75">(auto)</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Client info */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold mb-1">Client</p>
              <p className="font-semibold text-slate-800 flex items-center gap-1.5 truncate">
                <User className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                {client?.name || project?.propertyName || '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold mb-1">Phone</p>
              <p className="text-sm text-slate-700 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                {client?.phone ? String(client.phone) : '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold mb-1">Address</p>
              <p className="text-sm text-slate-700 truncate flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                {project?.propertyAddress || client?.address || '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold mb-1">Property Type</p>
              <p className="text-sm text-slate-700">{client?.propertyType || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-slate-800">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-emerald-700 font-medium text-xs uppercase tracking-wide">Category Type *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setCategoryType('residential')}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                  categoryType === 'residential'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Home className="w-4 h-4" /> Residential
              </button>
              <button
                type="button"
                onClick={() => setCategoryType('commercial')}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                  categoryType === 'commercial'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Building2 className="w-4 h-4" /> Commercial
              </button>
            </div>
          </div>
          <div>
            <Label className="text-emerald-700 font-medium text-xs uppercase tracking-wide">Main Category (Design Style) *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setMainCategory('Standard')}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${
                  mainCategory === 'Standard'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => setMainCategory('Luxury')}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${
                  mainCategory === 'Luxury'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Luxury
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blocks */}
      <div className="space-y-4">
        {blocks.map((block, blockIdx) => {
          const roomArea = getRoomArea(block);
          const blockSubtotal = getBlockSubtotal(block);
          const blockSubcat = subcatsById[block.subcategoryId];
          const blockProducts = allProducts.filter((p) => p.groupId === block.groupId);

          return (
            <Card key={block.id} className="border-0 shadow-md">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="bg-white/15 rounded-md px-2.5 py-1 text-white font-bold text-xs">
                    BLOCK #{blockIdx + 1}
                  </div>
                  {block.groupName && (
                    <Badge className="bg-emerald-500 text-white text-xs">{block.groupName}</Badge>
                  )}
                  {blockSubcat && (
                    <Badge className="bg-indigo-500 text-white text-xs">{blockSubcat.name}</Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => duplicateBlock(block.id)}
                    className="text-white/70 hover:text-white hover:bg-white/10 h-7 px-2 text-xs gap-1"
                  >
                    <Copy className="w-3 h-3" /> Duplicate
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => removeBlock(block.id)}
                    className="text-red-300 hover:text-red-100 hover:bg-red-500/20 h-7 px-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              <CardContent className="p-4 space-y-4">
                {/* Room + Subcat + Dimensions */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-emerald-700 text-xs font-medium">Room / Group *</Label>
                    <Select
                      value={block.groupId}
                      onValueChange={(v) => {
                        const g = groups.find((x) => x.id === v);
                        updateBlock(block.id, { groupId: v, groupName: g?.name || '' });
                      }}
                    >
                      <SelectTrigger className="border-emerald-200 mt-1">
                        <span className="text-sm truncate">{block.groupName || <span className="text-slate-400">Select room…</span>}</span>
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={4} className="max-h-[280px] overflow-y-auto z-50">
                        {groups.map((g) => (
                          <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-emerald-700 text-xs font-medium">Sub-Category *</Label>
                    <Select
                      value={block.subcategoryId}
                      onValueChange={(v) => updateBlock(block.id, { subcategoryId: v })}
                    >
                      <SelectTrigger className="border-emerald-200 mt-1">
                        <span className="text-sm truncate">{subcatsById[block.subcategoryId]?.name || <span className="text-slate-400">Select…</span>}</span>
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={4} className="max-h-[280px] overflow-y-auto z-50">
                        {subcategories.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Room Length (ft)</Label>
                    <Input
                      type="number" step="any"
                      value={block.roomLength}
                      onChange={(e) => updateBlock(block.id, { roomLength: e.target.value })}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Room Width (ft)</Label>
                    <div className="flex gap-2 items-center mt-1">
                      <Input
                        type="number" step="any"
                        value={block.roomWidth}
                        onChange={(e) => updateBlock(block.id, { roomWidth: e.target.value })}
                        placeholder="0"
                        className="flex-1"
                      />
                      <div className="text-xs font-bold text-emerald-700 whitespace-nowrap">
                        {roomArea > 0 ? `${roomArea} sqft` : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div className="border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      <Package className="w-4 h-4 text-emerald-600" />
                      Products ({block.items.length})
                    </p>
                    <Button
                      size="sm"
                      onClick={() => addItem(block.id)}
                      disabled={!block.groupId || !block.subcategoryId}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Product
                    </Button>
                  </div>

                  {(!block.groupId || !block.subcategoryId) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                      Select room and sub-category first to add products.
                    </div>
                  )}

                  <div className="space-y-2">
                    {block.items.map((item) => {
                      const product = productsById[item.productId];
                      const rate = getItemRate(block, item);
                      const amount = getItemAmount(block, item);
                      const productArea = getProductArea(item);
                      const productImage = product?.image
                        ? pb.files.getURL(product, product.image)
                        : null;

                      return (
                        <div
                          key={item.id}
                          className="flex flex-wrap items-end gap-2 bg-slate-50/60 p-2.5 rounded-lg border border-slate-100"
                        >
                          {/* Image */}
                          <div className="shrink-0">
                            {productImage ? (
                              <img
                                src={productImage}
                                alt={product?.name || ''}
                                className="w-10 h-10 rounded-md object-cover border border-slate-200"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center">
                                <ImageIcon className="w-4 h-4 text-slate-300" />
                              </div>
                            )}
                          </div>

                          {/* Product */}
                          <div className="flex-1 min-w-[180px]">
                            <Label className="text-[10px] text-slate-500 uppercase font-semibold">Product *</Label>
                            <Select
                              value={item.productId}
                              onValueChange={(v) => updateItem(block.id, item.id, { productId: v })}
                              disabled={!block.groupId || !block.subcategoryId}
                            >
                              <SelectTrigger className="h-9 mt-0.5 bg-white text-xs">
                                <span className="text-sm truncate">{productsById[item.productId]?.name || <span className="text-slate-400">Select product…</span>}</span>
                              </SelectTrigger>
                              <SelectContent position="popper" sideOffset={4} className="max-h-[280px] overflow-y-auto z-50">
                                {blockProducts.length === 0 ? (
                                  <div className="p-3 text-xs text-slate-400">No products in this room</div>
                                ) : (
                                  blockProducts.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                      <span className="font-mono text-[10px] text-emerald-600 mr-1.5">{p.itemId}</span>
                                      {p.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* L */}
                          <div className="w-20 shrink-0">
                            <Label className="text-[10px] text-slate-500 uppercase font-semibold">L (ft)</Label>
                            <Input
                              type="number" step="any"
                              value={item.length}
                              onChange={(e) => updateItem(block.id, item.id, { length: e.target.value })}
                              placeholder="0"
                              className="h-9 mt-0.5 bg-white text-xs text-center"
                            />
                          </div>

                          {/* W */}
                          <div className="w-20 shrink-0">
                            <Label className="text-[10px] text-slate-500 uppercase font-semibold">W (ft)</Label>
                            <Input
                              type="number" step="any"
                              value={item.width}
                              onChange={(e) => updateItem(block.id, item.id, { width: e.target.value })}
                              placeholder="0"
                              className="h-9 mt-0.5 bg-white text-xs text-center"
                            />
                          </div>

                          {/* Area */}
                          <div className="w-24 shrink-0">
                            <Label className="text-[10px] text-slate-500 uppercase font-semibold">Area</Label>
                            <div className="h-9 mt-0.5 flex items-center justify-center text-xs font-semibold text-emerald-700 bg-white border rounded-md">
                              {productArea > 0 ? productArea : '—'}
                            </div>
                          </div>

                          {/* Rate */}
                          <div className="w-28 shrink-0">
                            <Label className="text-[10px] text-slate-500 uppercase font-semibold">
                              Rate {item.customRate === '' && rate > 0 ? '(auto)' : ''}
                            </Label>
                            <Input
                              type="number" step="any"
                              value={item.customRate !== '' ? item.customRate : rate || ''}
                              onChange={(e) => updateItem(block.id, item.id, { customRate: e.target.value })}
                              className="h-9 mt-0.5 bg-white text-xs text-right"
                              placeholder="0"
                            />
                          </div>

                          {/* Amount */}
                          <div className="w-28 shrink-0">
                            <Label className="text-[10px] text-slate-500 uppercase font-semibold">Amount</Label>
                            <div className="h-9 mt-0.5 flex items-center justify-end text-sm font-bold text-emerald-700 pr-2 bg-white border rounded-md">
                              {amount > 0 ? rs(amount) : '—'}
                            </div>
                          </div>

                          {/* Delete */}
                          <div className="shrink-0">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => removeItem(block.id, item.id)}
                              disabled={block.items.length === 1}
                              className="h-9 px-2 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-end mt-3 pt-3 border-t border-slate-200">
                    <div className="text-right">
                      <p className="text-[10px] uppercase text-slate-500 font-semibold">Block Subtotal</p>
                      <p className="text-lg font-bold text-emerald-700">{rs(blockSubtotal)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add Block */}
      <Button
        onClick={addBlock}
        variant="outline"
        className="w-full border-2 border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-500 gap-2 py-6 text-base"
      >
        <Plus className="w-5 h-5" /> Add Another Block
      </Button>

      {/* Notes */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-slate-800">Notes / Terms</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Payment terms, delivery timeline, warranty info…"
          />
        </CardContent>
      </Card>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-200 p-4 shadow-2xl z-30 lg:pl-72">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm">
            <p className="text-slate-500">Grand Subtotal ({blocks.length} block{blocks.length !== 1 ? 's' : ''})</p>
            <p className="text-xl font-bold text-emerald-700">{rs(grandTotal)}</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={isSaving}
              className="flex-1 sm:flex-none"
            >
              Back to List
            </Button>
            <Button
              onClick={() => handleSave('Draft')}
              disabled={isSaving || grandTotal <= 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-1 sm:flex-none"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isSaving ? 'Saving…' : isEditing ? 'Update Quotation' : 'Save Quotation'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteriorQuotationForm;
